import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn";

/** Slack before an edge counts as reached (sub-pixel scroll positions). */
const EDGE_PX = 2;
/** Room kept past the active pill so the right arrow doesn't cover it. */
const ACTIVE_MARGIN_PX = 40;

/**
 * A horizontally scrolling row (category pills) with an arrow at each end
 * that has more to show. Swipe, trackpad and keyboard focus scroll it; the
 * arrows are for a mouse, which has no other way past the edge. The child
 * marked `aria-pressed="true"` starts in view.
 */
export function ScrollRow({
  children,
  label,
  className,
}: {
  children: ReactNode;
  /** What the row holds, for the arrows' names ("Scroll categories left"). */
  label: string;
  className?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = contentRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (active) {
      const end = active.offsetLeft + active.offsetWidth;
      if (end > el.clientWidth) el.scrollLeft = end - el.clientWidth + ACTIVE_MARGIN_PX;
    }
    const update = () => {
      const left = el.scrollLeft > EDGE_PX;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_PX;
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
    };
    const frame = requestAnimationFrame(update);
    el.addEventListener("scroll", update, { passive: true });
    // The content box changes width when a pill is added or removed (the
    // "My imports" tab), which doesn't resize the scroller itself.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    ro?.observe(el);
    if (contentRef.current) ro?.observe(contentRef.current);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, []);

  const scroll = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = dir * Math.max(el.clientWidth * 0.75, 96);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (typeof el.scrollBy === "function") {
      el.scrollBy({ left, behavior: reduce ? "auto" : "smooth" });
    } else {
      el.scrollLeft += left;
    }
  };

  return (
    <div className={cn("relative", className)}>
      <div ref={scrollerRef} className="relative overflow-x-auto no-scrollbar" data-testid="scroll-row">
        <div ref={contentRef} className="flex w-max gap-1.5">
          {children}
        </div>
      </div>
      {more.left && <EdgeArrow side="left" label={label} onClick={() => scroll(-1)} />}
      {more.right && <EdgeArrow side="right" label={label} onClick={() => scroll(1)} />}
    </div>
  );
}

function EdgeArrow({
  side,
  label,
  onClick,
}: {
  side: "left" | "right";
  label: string;
  onClick: () => void;
}) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-y-0 flex items-center from-white from-50% to-transparent",
        side === "left" ? "left-0 pr-6 bg-gradient-to-r" : "right-0 pl-6 bg-gradient-to-l"
      )}
    >
      {/* Focus and swipe already scroll the row, so the arrows stay out of
          the tab order and the accessibility tree. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        aria-label={`Scroll ${label} ${side}`}
        data-testid={`scroll-row-${side}`}
        onClick={onClick}
        className="pointer-events-auto grid size-8 place-items-center rounded-full bg-white text-ink-700 shadow-sm ring-1 ring-ink-200 transition-colors hover:bg-ink-50 hover:text-ink-900 active:bg-ink-100"
      >
        <Icon className="size-4" />
      </button>
    </div>
  );
}
